"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";
import { lowercaseWords } from "@/lib/tool-kit/text-analysis/tokens";

const TOP_N = 15;
const MIN_COUNT = 2;

/** Common English function words — a phrase made ENTIRELY of these is a "stop phrase". */
const STOP_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "or",
  "but",
  "nor",
  "so",
  "yet",
  "of",
  "in",
  "on",
  "at",
  "to",
  "by",
  "for",
  "with",
  "from",
  "as",
  "into",
  "onto",
  "over",
  "under",
  "about",
  "after",
  "before",
  "between",
  "through",
  "during",
  "is",
  "am",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
  "do",
  "does",
  "did",
  "have",
  "has",
  "had",
  "will",
  "would",
  "can",
  "could",
  "shall",
  "should",
  "may",
  "might",
  "must",
  "it",
  "its",
  "this",
  "that",
  "these",
  "those",
  "there",
  "here",
  "he",
  "she",
  "they",
  "we",
  "you",
  "i",
  "not",
  "no",
  "if",
  "then",
  "than",
  "when",
  "while",
  "which",
  "who",
  "what",
]);

interface PhraseCount {
  phrase: string;
  count: number;
}

/** Letters in any script, digits and internal apostrophes (straight or curly); a hyphen splits. */
export function tokenize(text: string): string[] {
  return lowercaseWords(text, { hyphens: "split" });
}

function isStopPhrase(phrase: string): boolean {
  return phrase.split(" ").every((word) => STOP_WORDS.has(word));
}

/** Count all n-word phrases and return the top repeated ones. */
export function countNgrams(
  tokens: string[],
  n: number,
  filterStopPhrases: boolean
): PhraseCount[] {
  const counts = new Map<string, number>();
  for (let i = 0; i + n <= tokens.length; i++) {
    const phrase = tokens.slice(i, i + n).join(" ");
    counts.set(phrase, (counts.get(phrase) ?? 0) + 1);
  }
  return [...counts.entries()]
    .filter(([phrase, count]) => {
      if (count < MIN_COUNT) {
        return false;
      }
      if (filterStopPhrases && isStopPhrase(phrase)) {
        return false;
      }
      return true;
    })
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, TOP_N)
    .map(([phrase, count]) => ({ phrase, count }));
}

/**
 * The clipboard form. ASCII digits, deliberately not localized — a copied count is
 * pasted into a spreadsheet or a document as often as it is read.
 */
function buildCopyText(
  bigrams: PhraseCount[],
  trigrams: PhraseCount[],
  strings: { twoWordPhrases: string; threeWordPhrases: string }
): string {
  const lines: string[] = [strings.twoWordPhrases];
  for (const entry of bigrams) {
    lines.push(`${entry.phrase} — ${entry.count}`);
  }
  lines.push("", strings.threeWordPhrases);
  for (const entry of trigrams) {
    lines.push(`${entry.phrase} — ${entry.count}`);
  }
  return lines.join("\n");
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

function PhraseTable({
  title,
  phrases,
  strings,
}: {
  title: string;
  phrases: PhraseCount[];
  strings: {
    noPhrases: string;
    hashColumn: string;
    phraseColumn: string;
    countColumn: string;
  };
}) {
  const fmt = useToolNumber();
  return (
    <div className="space-y-3">
      <h3 className="font-medium text-foreground">{title}</h3>
      {phrases.length === 0 ? (
        <p className="rounded-lg border border-border bg-card p-4 text-muted-foreground text-sm">
          {t(strings.noPhrases, { min: MIN_COUNT })}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border border-b bg-muted/30">
                <th className="p-3 font-medium text-foreground">
                  {strings.hashColumn}
                </th>
                <th className="p-3 font-medium text-foreground">
                  {strings.phraseColumn}
                </th>
                <th className="p-3 font-medium text-foreground">
                  {strings.countColumn}
                </th>
              </tr>
            </thead>
            <tbody>
              {phrases.map((entry, index) => (
                <tr className="border-border border-b" key={entry.phrase}>
                  <td className="p-3 text-muted-foreground">{index + 1}</td>
                  <td className="p-3 font-mono text-foreground">
                    {entry.phrase}
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {fmt(entry.count)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const PHRASE_FREQUENCY_COUNTER_STRINGS = {
  yourText: "Your text",
  inputPlaceholder:
    "Paste an essay, article, or draft here to find repeated phrases...",
  hideStopPhrases: "Hide stop phrases",
  stopPhrasesExplainer:
    '(phrases made entirely of common function words like "of the" or "it is")',
  countPhrases: "Count Phrases",
  copied: "Copied!",
  copyResults: "Copy Results",
  clear: "Clear",
  wordsAnalyzed: "Words analyzed",
  repeated2WordPhrases: "Repeated 2-word phrases",
  repeated3WordPhrases: "Repeated 3-word phrases",
  topRepeatedPhrases: "Top repeated phrases",
  twoWordPhrasesTitle: "Two-word phrases (top {top}, used {min}+ times)",
  threeWordPhrasesTitle: "Three-word phrases (top {top}, used {min}+ times)",
  noPhrases: "No phrases of this length appear at least {min} times.",
  hashColumn: "#",
  phraseColumn: "Phrase",
  countColumn: "Count",
  twoWordPhrasesCopyHeading: "Two-word phrases:",
  threeWordPhrasesCopyHeading: "Three-word phrases:",
} as const;

export default function PhraseFrequencyCounterWidget() {
  const [input, setInput] = useState("");
  const [filterStopPhrases, setFilterStopPhrases] = useState(true);
  const [results, setResults] = useState<{
    wordCount: number;
    bigrams: PhraseCount[];
    trigrams: PhraseCount[];
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(PHRASE_FREQUENCY_COUNTER_STRINGS);

  const fmt = useToolNumber();

  function runCount(text: string, filter: boolean) {
    const tokens = tokenize(text);
    if (tokens.length === 0) {
      return;
    }
    setResults({
      wordCount: tokens.length,
      bigrams: countNgrams(tokens, 2, filter),
      trigrams: countNgrams(tokens, 3, filter),
    });
    setCopied(false);
  }

  function handleCount() {
    runCount(input, filterStopPhrases);
  }

  function handleToggleFilter(checked: boolean) {
    setFilterStopPhrases(checked);
    if (results) {
      runCount(input, checked);
    }
  }

  function handleCopy() {
    if (!results) {
      return;
    }
    writeToClipboard(
      buildCopyText(results.bigrams, results.trigrams, {
        twoWordPhrases: strings.twoWordPhrasesCopyHeading,
        threeWordPhrases: strings.threeWordPhrasesCopyHeading,
      })
    ).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setResults(null);
    setCopied(false);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="phrase-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[240px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="phrase-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Options */}
      <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-sm">
        <input
          checked={filterStopPhrases}
          className="accent-primary"
          onChange={(e) => handleToggleFilter(e.target.checked)}
          type="checkbox"
        />
        <span className="text-foreground">
          {strings.hideStopPhrases}{" "}
          <span className="text-muted-foreground">
            {strings.stopPhrasesExplainer}
          </span>
        </span>
      </label>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleCount}
          type="button"
        >
          {strings.countPhrases}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={results === null}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResults}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && results === null}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Stats */}
      {results && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={strings.wordsAnalyzed}
            value={fmt(results.wordCount)}
          />
          <StatCard
            label={strings.repeated2WordPhrases}
            value={results.bigrams.length}
          />
          <StatCard
            label={strings.repeated3WordPhrases}
            value={results.trigrams.length}
          />
        </div>
      )}

      {/* Results */}
      {results && (
        <section className="space-y-6">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.topRepeatedPhrases}
          </h2>
          <PhraseTable
            phrases={results.bigrams}
            strings={strings}
            title={t(strings.twoWordPhrasesTitle, {
              top: TOP_N,
              min: MIN_COUNT,
            })}
          />
          <PhraseTable
            phrases={results.trigrams}
            strings={strings}
            title={t(strings.threeWordPhrasesTitle, {
              top: TOP_N,
              min: MIN_COUNT,
            })}
          />
        </section>
      )}
    </>
  );
}
